import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import {
  getMatterSettings,
  updateMatterSettings,
  type RafOrgSettings,
} from '../../actions/matters/rafSettings';

const DEFAULTS: RafOrgSettings = {
  notifyNewMatter: false,
  intelligentModeDefault: false,
  notificationEmail: '',
};

const SettingsView: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<RafOrgSettings>(DEFAULTS);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = await getMatterSettings();
        setSettings(s);
      } catch {
        setError('Failed to load settings.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccessMsg('');
    setSaving(true);
    try {
      const saved = await updateMatterSettings(settings);
      setSettings(saved);
      setSuccessMsg('Settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: 'var(--teal)' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 720,
        mx: 'auto',
        px: { xs: 1.5, sm: 2, md: 3 },
        pt: { xs: 2.5, sm: 3.5 },
        pb: 8,
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/matters')}
        sx={{
          color: 'var(--teal)',
          textTransform: 'none',
          mb: 2,
          pl: 0,
          '&:hover': { bgcolor: 'transparent', color: 'var(--teal-mid)' },
        }}
      >
        Back to Matters
      </Button>
      <Typography
        sx={{
          fontFamily: "'Syne', sans-serif",
          fontSize: '1.4rem',
          fontWeight: 700,
          color: 'var(--t1)',
        }}
      >
        Settings
      </Typography>
      <Typography sx={{ color: 'var(--t3)', fontSize: '0.82rem', mt: 0.5, mb: 3 }}>
        Organisation-level RAF Scribe preferences
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      <Box
        sx={{
          bgcolor: 'var(--s2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={settings.notifyNewMatter}
              onChange={(e) =>
                setSettings((s) => ({ ...s, notifyNewMatter: e.target.checked }))
              }
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--t1)' }}>
                New matter notifications
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'var(--t3)' }}>
                Send an email whenever a new matter is registered.
              </Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={
            <Switch
              checked={settings.intelligentModeDefault}
              onChange={(e) =>
                setSettings((s) => ({
                  ...s,
                  intelligentModeDefault: e.target.checked,
                }))
              }
            />
          }
          label={
            <Box>
              <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--t1)' }}>
                Intelligent Mode default
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'var(--t3)' }}>
                When on, extraction uses richer reasoning by default.
              </Typography>
            </Box>
          }
        />

        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--t2)', mb: 1 }}>
            Notification email
          </Typography>
          <TextField
            value={settings.notificationEmail}
            onChange={(e) =>
              setSettings((s) => ({ ...s, notificationEmail: e.target.value }))
            }
            placeholder="alerts@example.com"
            size="small"
            fullWidth
            type="email"
          />
          <Typography sx={{ fontSize: '0.74rem', color: 'var(--t4)', mt: 0.5 }}>
            Leave blank to fall back to the default contact address.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <SaveIcon />
              )
            }
            disabled={saving}
            onClick={handleSave}
            sx={{
              bgcolor: 'var(--teal)',
              color: 'var(--bg)',
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              borderRadius: 'var(--radius-sm)',
              px: 2.5,
              '&:hover': { bgcolor: 'var(--teal-mid)' },
            }}
          >
            {saving ? 'Saving...' : 'Save settings'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsView;
